import React, { useState, useMemo, useEffect } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Copy, Trash2, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RagPill } from './RagPill';
import { EditableCell } from './EditableCell';
import { Progress } from './ui/progress';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

const SORT_COLS = [
  { key: 'rag', label: 'Status' },
  { key: 'name', label: 'Project' },
  { key: 'phase', label: 'Phase' },
  { key: 'owner', label: 'Owner' },
  { key: 'pct', label: '% Complete' },
  { key: 'latest_update', label: 'Latest Update' },
];

const RAG_ORDER = { R: 0, A: 1, G: 2, B: 3 };

function PctInput({ value, onCommit, projectId }) {
  const [draft, setDraft] = useState(value ?? 0);
  useEffect(() => { setDraft(value ?? 0); }, [value]);
  const commit = () => {
    const val = Math.max(0, Math.min(100, parseInt(draft) || 0));
    if (val !== (value ?? 0)) onCommit(val);
    else setDraft(val);
  };
  return (
    <input
      type="number"
      min="0"
      max="100"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
      className="w-12 text-xs tabular-nums text-right bg-transparent border rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))]"
      data-testid={`project-table-cell-pct-${projectId}`}
    />
  );
}

export function ProjectTable({ projects = [], workstreams = [], boardId, onRefresh, readOnly = false }) {
  const [sortCol, setSortCol] = useState('sort_order');
  const [sortDir, setSortDir] = useState('asc');
  const [filter, setFilter] = useState('all');
  const { authedFetch } = useAuth();

  const filtered = useMemo(() => {
    let list = [...projects];
    if (filter !== 'all') {
      list = list.filter((p) => p.rag === filter);
    }
    list.sort((a, b) => {
      let va = a[sortCol];
      let vb = b[sortCol];
      if (sortCol === 'rag') {
        va = RAG_ORDER[va] ?? 99;
        vb = RAG_ORDER[vb] ?? 99;
      }
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [projects, sortCol, sortDir, filter]);

  const toggleSort = (col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };

  const handlePatch = async (projectId, field, value) => {
    try {
      await authedFetch(`/api/projects/${projectId}`, {
        method: 'PATCH',
        body: JSON.stringify({ [field]: value }),
      });
      onRefresh?.();
    } catch (e) {
      toast.error(`Failed to update ${field}`);
    }
  };

  const handleDuplicate = async (projectId) => {
    try {
      await authedFetch(`/api/projects/${projectId}/duplicate`, { method: 'POST' });
      onRefresh?.();
      toast.success('Project duplicated');
    } catch (e) {
      toast.error('Failed to duplicate');
    }
  };

  const handleDelete = async (projectId, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await authedFetch(`/api/projects/${projectId}`, { method: 'DELETE' });
      onRefresh?.();
      toast.success('Project deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleAdd = async () => {
    try {
      await authedFetch(`/api/boards/${boardId}/projects`, {
        method: 'POST',
        body: JSON.stringify({ name: 'New Project', rag: 'B' }),
      });
      onRefresh?.();
      toast.success('Project added');
    } catch (e) {
      toast.error('Failed to add project');
    }
  };

  const handleWorkstreamChange = async (projectId, wsId) => {
    await handlePatch(projectId, 'workstream_id', wsId === 'none' ? null : wsId);
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2" data-testid="board-toolbar">
        {[{ key: 'all', label: 'All' }, { key: 'G', label: 'On Track' }, { key: 'A', label: 'Caution' }, { key: 'R', label: 'At Risk' }, { key: 'B', label: 'Not Started' }].map((f) => (
          <Button
            key={f.key}
            variant={filter === f.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f.key)}
            data-testid={`board-toolbar-rag-filter-${f.key}`}
            className="text-xs"
          >
            {f.label}
          </Button>
        ))}

        {!readOnly && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleAdd}
            className="ml-auto gap-1.5"
            data-testid="add-project-button"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Project
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-xl border bg-card shadow-sm overflow-hidden" data-testid="project-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="w-8" />
                {SORT_COLS.map((col) => (
                  <th
                    key={col.key}
                    className="px-3 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors select-none"
                    onClick={() => toggleSort(col.key)}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      <SortIcon col={col.key} />
                    </span>
                  </th>
                ))}
                {!readOnly && <th className="w-20 px-3 py-3" />}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={readOnly ? 8 : 9} className="text-center py-12 text-muted-foreground">
                    No projects found
                  </td>
                </tr>
              )}
              {filtered.map((p) => {
                const wsColor = p.workstream?.color || '#e2e8f0';
                return (
                  <tr
                    key={p.id}
                    className="border-b last:border-0 hover:bg-muted/40 transition-colors group"
                    data-testid={`project-row-${p.id}`}
                  >
                    {/* Workstream color stripe */}
                    <td className="relative w-1 p-0">
                      <div className="absolute left-0 top-0 h-full w-[3px]" style={{ backgroundColor: wsColor }} />
                    </td>

                    {/* RAG */}
                    <td className="px-3 py-2">
                      <RagPill
                        rag={p.rag}
                        projectId={p.id}
                        readOnly={readOnly}
                        onClick={(next) => handlePatch(p.id, 'rag', next)}
                      />
                    </td>

                    {/* Name */}
                    <td className="px-3 py-2 min-w-[180px]">
                      <EditableCell
                        value={p.name}
                        field="name"
                        projectId={p.id}
                        readOnly={readOnly}
                        onSave={(v) => handlePatch(p.id, 'name', v)}
                        className="font-medium"
                      />
                    </td>

                    {/* Phase */}
                    <td className="px-3 py-2 min-w-[100px]">
                      <EditableCell
                        value={p.phase}
                        field="phase"
                        projectId={p.id}
                        readOnly={readOnly}
                        onSave={(v) => handlePatch(p.id, 'phase', v)}
                      />
                    </td>

                    {/* Owner */}
                    <td className="px-3 py-2 min-w-[120px]">
                      <EditableCell
                        value={p.owner}
                        field="owner"
                        projectId={p.id}
                        readOnly={readOnly}
                        onSave={(v) => handlePatch(p.id, 'owner', v)}
                      />
                    </td>

                    {/* % Complete */}
                    <td className="px-3 py-2 min-w-[140px]">
                      <div className="flex items-center gap-2" data-testid={`project-progress-${p.id}`}>
                        <Progress value={p.pct} className="h-2 flex-1" />
                        {readOnly ? (
                          <span className="text-xs tabular-nums w-8 text-right">{p.pct}%</span>
                        ) : (
                          <PctInput
                            value={p.pct}
                            projectId={p.id}
                            onCommit={(val) => handlePatch(p.id, 'pct', val)}
                          />
                        )}
                      </div>
                    </td>

                    {/* Latest Update */}
                    <td className="px-3 py-2 min-w-[200px] max-w-[350px]">
                      <EditableCell
                        value={p.latest_update}
                        field="latest_update"
                        projectId={p.id}
                        readOnly={readOnly}
                        multiline
                        onSave={(v) => handlePatch(p.id, 'latest_update', v)}
                      />
                    </td>

                    {/* Actions */}
                    {!readOnly && (
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {workstreams.length > 0 && (
                            <Select
                              value={p.workstream_id || 'none'}
                              onValueChange={(v) => handleWorkstreamChange(p.id, v)}
                            >
                              <SelectTrigger className="h-7 w-7 p-0 border-0 bg-transparent">
                                <SelectValue>
                                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: wsColor }} />
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                {workstreams.map((ws) => (
                                  <SelectItem key={ws.id} value={ws.id}>
                                    <span className="flex items-center gap-2">
                                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: ws.color }} />
                                      {ws.name}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleDuplicate(p.id)}
                            data-testid={`duplicate-project-${p.id}`}
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => handleDelete(p.id, p.name)}
                            data-testid={`delete-project-${p.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
